













































import {
  defineComponent,
  onErrorCaptured,
  ref,
  unref,
  computed,
  watch,
  onBeforeUnmount,
  Ref,
  h,
} from '@vue/composition-api';
import { useQueryProvider } from 'vue-query';
import { ApiCacheContext } from '@services/ApiCacheContext';
import { GlobalApiErrorContext } from '@services/GlobalApiErrorContext';
import StyleOverrideContext from '@berlin-front/common/services/StyleOverrideContext';
import { ThemeContext } from '@berlin-front/common/services/ThemeContext';
import { BerlinBffContext } from '@services/BerlinBff';
import ContextProviderRouterView from '@berlin-front/common/components/elements/ContextProviderRouterView';
import HelpView from '@views/help';
import type Vue from 'vue';
import AppClose from '@berlin-front/common/components/elements/AppClose';
import AlertModal, { MODAL_STATE } from '@berlin-front/common/components/clusters/AlertModal';
import { RouteMeta } from 'vue-router';
import { ModalHeaderContext } from '@services/ModalHeaderContext';
import { ModalParameterContext } from '@/services/ModalParameterContext';
import { ModalBridgeContext } from '@/services/ModalBridgeContext';
import { bemx, isAxiosError, simpleSetValue } from '@berlin-front/common/utils';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { broadcast } from './broadcast';

export const MODAL_ON_MODAL = {
  CANCEL_MODAL: {
    ApplyButtonComponent: { render: () => h('span', {}, ['戻る']) },
    CancelButtonComponent: { render: () => h('span', {}, ['キャンセル']) },
    MessageComponent: {
      render: () => h('span', {}, ['お店の画面に戻ります。よろしいですか？入力内容は破棄されます。']),
    },
  },
};

const getElement = (containerRef: Ref<Vue | Element | undefined>): Element | undefined => {
  if (!containerRef.value) {
    return undefined;
  }
  if (containerRef.value instanceof Element) {
    return containerRef.value;
  }
  if (containerRef.value.$el) {
    return containerRef.value.$el;
  }
  return undefined;
};

export default defineComponent({
  name: 'App',
  components: {
    ApiCacheContextProvider: ApiCacheContext.provider,
    BerlinBffContextProvider: BerlinBffContext.provider,
    ContextProviderRouterView,
    GlobalApiErrorContextProvider: GlobalApiErrorContext.provider,
    ModalBridgeContextProvider: ModalBridgeContext.provider,
    ModalBridgeContextConsumer: ModalBridgeContext.consumer,
    ModalParameterContextProvider: ModalParameterContext.provider,
    StyleOverrideContext,
    ThemeContextProvider: ThemeContext.provider,
    AlertModal,
    AppClose,
    HelpView,
    HeaderContextProvider: ModalHeaderContext.provider,
  },

  setup() {
    // ヘルプページの表示を管理
    const isShowHelp = ref(false);
    const setIsShowHelp = simpleSetValue(isShowHelp);

    const router = useRouter();
    // 内部でやってることはprovide/inject apiのprovide。
    // キャッシュ機構なんかの提供を行うことで、子孫コンポーネントでuseQueryが使える。
    // ElementになってないだけでReactQueryのプロバイダと同じ。
    const handleRequestParameterError = (error: unknown) => {
      if (error instanceof Error && !isAxiosError(error)) {
        // vue-queryのクロージャで発生するエラーのうち、js例外はパラメータ不正なのでショップエラーとして扱って良い。
        // axiosエラーは `@service/BerlinBff` でハンドルしてる。
        broadcast.send({
          type: 'JS_ERROR',
          payload: {
            name: error.name,
            message: error.message,
            stack: error.stack ?? '',
          },
        });
        broadcast.send({ type: 'API_ERROR', payload: { url: '', errorType: 'SYSTEM_ERROR' } });
      }
    };

    useQueryProvider({
      defaultOptions: {
        mutations: {
          onError: handleRequestParameterError,
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          refetchInterval: false,
          refetchOnReconnect: false,
          refetchOnMount: false,
          cacheTime: 0,
          staleTime: 0,
          onError: handleRequestParameterError,
          retry: false,
        },
      },
    } as const);

    // フレームワーク上キャッチ可能なエラーは一旦topレベルでハンドリングし、
    // postMessageで自身に配信する。
    // messageは再度、ショップサイトとのブリッジ部分でハンドリングし、shopサイトと、error apiに送信する。
    onErrorCaptured((error: Error, _vm: Vue, _info: string) => {
      broadcast.send({
        type: 'JS_ERROR',
        payload: {
          name: error.name,
          message: error.message,
          stack: error.stack ?? '',
        },
      });
      broadcast.send({ type: 'API_ERROR', payload: { url: '', errorType: 'SYSTEM_ERROR' } });
    });

    // ページ遷移時のスクロール制御。トップ位置に戻す。
    const scrollContainer = ref<Vue | Element>();
    onBeforeUnmount(
      router.afterEach(() => {
        const element = getElement(scrollContainer);
        if (element) {
          element.scrollTop = 0;
        }
      }) as () => void,
    );

    /*
    × ボタン離脱前モーダル制御
    */
    const modal = ref<InstanceType<typeof AlertModal>>();
    const modalType = ref<RouteMeta['cancelModalType']>();
    const headerTitle = ref<RouteMeta['headerTitle']>();
    const headerType = ref<RouteMeta['headerType']>();
    const showDialog = computed(() => modal.value?.show || (() => {}));
    const hideScrollBar = computed(() => (modal.value ? modal.value.value : false));
    router.afterEach((to) => {
      // 画面遷移するたびに、その画面にルートレベルで指定しているキャンセルモーダルのタイプを記録する。
      modalType.value = to.meta?.cancelModalType;
      headerTitle.value = to.meta?.headerTitle;
      headerType.value = to.meta?.headerType;

      // ローディング中に閉じようとした場合、画面遷移したタイミングで状況が変わったとみなし、モーダルを閉じる。多分その方が決済を促す意味で良さそう。
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      modal.value?.hide?.();
    });
    // ×ボタンの押下イベントを検出する。
    const cancel = () => {
      const modalTypeValue = modalType.value;
      // モーダルタイプが定義されてない画面はそのままキャンセル処理を行う。
      if (modalTypeValue === undefined) {
        broadcast.send({ type: 'MODAL_CONTROL_CANCELLED', payload: {} });
        return;
      }
      // 決済中は×ボタン無視。受け付けない。
      if (modalTypeValue === 'reject') {
        return;
      }
      // それ以外のケースではモーダル表示。
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      showDialog?.value?.(MODAL_ON_MODAL.CANCEL_MODAL);
    };
    // モーダルで警告を蹴ってAPPLYしたことをハンドリングする。
    watch(
      () => modal.value?.state ?? MODAL_STATE.IDLE,
      (state) => {
        if (state === MODAL_STATE.APPLY) {
          broadcast.send({ type: 'MODAL_CONTROL_CANCELLED', payload: {} });
        }
      },
    );

    /*
      stateをスタイルに適用。
    */
    const css = computed(() =>
      bemx('NpApp', {
        // モーダルonモーダルが表示されてる時、スクロールバーを消す。
        // スクロールバーが表示されてると下のモーダルと上のモーダルの中央がズレ、見た目相当悪くなるため。
        hideScrollBar: unref(hideScrollBar),
      }),
    );

    return {
      scrollContainer,
      cancel,
      css,
      modal,
      showDialog,
      headerTitle,
      headerType,
      globalContextValue: { isShowHelp, setIsShowHelp },
      computed,
    };
  },
});
